import React, { useState } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon } from './icons/PlaybackIcons';
import { useAuth } from '../contexts/AuthContext';

const Navbar: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const activeLinkStyle = {
        color: '#22d3ee', // cyan-400
        borderBottom: '2px solid #22d3ee'
    };
    
    const linkStyle = "px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white transition-colors";

    const handleProfileClick = () => {
        setIsOpen(false);
        navigate('/profile');
    };

    return (
        <nav className="bg-gray-800 border-b border-gray-700 flex-shrink-0">
            <div className="px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <div className="flex items-center">
                        <Link to="/" className="flex-shrink-0 text-white text-xl font-bold cursor-pointer hover:opacity-90 transition-opacity">
                            RaceLine<span className="text-cyan-400">IQ</span>
                        </Link>
                        <div className="hidden md:block">
                            <div className="ml-10 flex items-baseline space-x-4">
                                <NavLink 
                                    to="/" 
                                    className={({ isActive }) => `${linkStyle} ${isActive ? 'text-cyan-400' : ''}`}
                                    style={({isActive}) => isActive ? activeLinkStyle : {}}
                                >
                                    Find Events
                                </NavLink>
                                {currentUser && (
                                    <NavLink 
                                        to="/races" 
                                        className={({ isActive }) => `${linkStyle} ${isActive ? 'text-cyan-400' : ''}`}
                                        style={({isActive}) => isActive ? activeLinkStyle : {}}
                                    >
                                        Your Events
                                    </NavLink>
                                )}
                                <NavLink 
                                    to="/about" 
                                    className={({ isActive }) => `${linkStyle} ${isActive ? 'text-cyan-400' : ''}`}
                                    style={({isActive}) => isActive ? activeLinkStyle : {}}
                                >
                                    About
                                </NavLink>
                                <NavLink 
                                    to="/how-to-upload" 
                                    className={({ isActive }) => `${linkStyle} ${isActive ? 'text-cyan-400' : ''}`}
                                    style={({isActive}) => isActive ? activeLinkStyle : {}}
                                >
                                    How to Upload
                                </NavLink>
                            </div>
                        </div>
                    </div>
                    
                    <div className="hidden md:flex items-center gap-4">
                        {currentUser ? (
                            <div className="flex items-center gap-3">
                                <div className="flex flex-col items-end cursor-pointer" onClick={handleProfileClick}>
                                    <span className="text-sm text-white font-medium hover:text-cyan-400 transition-colors">{currentUser.displayName || 'User'}</span>
                                    <span className="text-xs text-gray-400">{currentUser.email}</span>
                                </div>
                                <img 
                                    src={currentUser.photoURL || `https://ui-avatars.com/api/?name=${currentUser.displayName}`} 
                                    alt="Profile" 
                                    className="w-8 h-8 rounded-full border-2 border-gray-600 cursor-pointer hover:border-cyan-400 transition-colors"
                                    onClick={handleProfileClick}
                                />
                                <button 
                                    onClick={() => logout()}
                                    className="ml-2 px-3 py-1 text-xs font-medium text-gray-300 border border-gray-600 rounded hover:bg-gray-700 hover:text-white transition-colors"
                                >
                                    Logout
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <Link
                                    to={`/auth?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                    className="text-sm font-semibold text-gray-300 hover:text-white transition-colors px-2 py-1"
                                >
                                    Sign In
                                </Link>
                                <Link
                                    to={`/auth?mode=signup&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                    className="px-4 py-2 text-xs font-bold text-gray-950 bg-cyan-400 hover:bg-cyan-300 rounded-lg transition-colors shadow-sm shadow-cyan-400/20 active:scale-95"
                                >
                                    Create Account
                                </Link>
                            </div>
                        )}
                    </div>

                    <div className="-mr-2 flex md:hidden">
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            type="button"
                            className="bg-gray-800 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                            aria-controls="mobile-menu"
                            aria-expanded="false"
                        >
                            <span className="sr-only">Open main menu</span>
                            {!isOpen ? (
                                <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                            ) : (
                                <XIcon className="block h-6 w-6" aria-hidden="true" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {isOpen && (
                <div className="md:hidden" id="mobile-menu">
                    <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                         <NavLink 
                            to="/" 
                            className={({ isActive }) => `block ${linkStyle} ${isActive ? 'bg-gray-900 text-white' : ''}`}
                            style={({isActive}) => isActive ? {color: '#22d3ee'} : {}}
                            onClick={() => setIsOpen(false)}
                         >
                            Find Events
                        </NavLink>
                        {currentUser && (
                            <NavLink 
                                to="/races" 
                                className={({ isActive }) => `block ${linkStyle} ${isActive ? 'bg-gray-900 text-white' : ''}`}
                                style={({isActive}) => isActive ? {color: '#22d3ee'} : {}}
                                onClick={() => setIsOpen(false)}
                            >
                                Your Events
                            </NavLink>
                        )}
                        <NavLink 
                            to="/about" 
                            className={({ isActive }) => `block ${linkStyle} ${isActive ? 'bg-gray-900 text-white' : ''}`}
                            style={({isActive}) => isActive ? {color: '#22d3ee'} : {}}
                            onClick={() => setIsOpen(false)}
                         >
                            About
                        </NavLink>
                        <NavLink 
                            to="/how-to-upload" 
                            className={({ isActive }) => `block ${linkStyle} ${isActive ? 'bg-gray-900 text-white' : ''}`}
                            style={({isActive}) => isActive ? {color: '#22d3ee'} : {}}
                            onClick={() => setIsOpen(false)}
                         >
                            How to Upload
                        </NavLink>
                        
                        <div className="border-t border-gray-700 mt-4 pt-4 pb-2">
                            {currentUser ? (
                                <div className="flex flex-col gap-2 px-3">
                                    <div className="flex items-center gap-3 mb-2 cursor-pointer" onClick={handleProfileClick}>
                                        <img 
                                            src={currentUser.photoURL || ''} 
                                            alt="Profile" 
                                            className="w-8 h-8 rounded-full"
                                        />
                                        <span className="text-white font-medium">{currentUser.displayName || 'User'}</span>
                                    </div>
                                    <button
                                        onClick={handleProfileClick}
                                        className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700"
                                    >
                                        Edit Profile
                                    </button>
                                    <button 
                                        onClick={() => { logout(); setIsOpen(false); }}
                                        className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-400 hover:text-white hover:bg-gray-700"
                                    >
                                        Logout
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2 px-3">
                                    <Link
                                        to={`/auth?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                        onClick={() => setIsOpen(false)}
                                        className="w-full text-center block px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-200 bg-gray-700 hover:bg-gray-600"
                                    >
                                        Sign In
                                    </Link>
                                    <Link
                                        to={`/auth?mode=signup&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                        onClick={() => setIsOpen(false)}
                                        className="w-full text-center block px-4 py-2.5 rounded-lg text-sm font-bold text-gray-950 bg-cyan-400 hover:bg-cyan-300"
                                    >
                                        Create Account
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;